'use client'

import { hyperEVM } from '@/config/chain/constants'
import { Button } from '@/ui/atoms/button/Button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { Typography } from '@/ui/atoms/typography/Typography'
import { AlertTriangle, Check, Loader2, Trash2, Zap } from 'lucide-react'
import { useMemo, useState } from 'react'
import { STABLECOIN_LABELS, STABLECOIN_TOKENS } from '../constants'
import { useDeleteCardHook, useUpdateCardHook } from '../hooks/useCardHook'
import type { UserCard } from '../hooks/useUserCards'

interface HookManagementProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  card: UserCard
  mode: 'add' | 'edit' | 'remove'
}

const TOKEN_OPTIONS = Object.entries(STABLECOIN_TOKENS).map(([, address]) => ({
  address: address as string,
  label: STABLECOIN_LABELS[address.toLowerCase()] ?? address.slice(0, 10),
}))

export function HookManagement({ open, onOpenChange, card, mode }: HookManagementProps) {
  const { updateHook, isPending: isUpdating, error: updateError, reset: resetUpdate } = useUpdateCardHook()
  const { deleteHook, isPending: isDeleting, error: deleteError } = useDeleteCardHook()

  const existingConfig = card.hookConfig as { inputToken?: string; outputToken?: string; slippageBps?: number } | null

  const [inputToken, setInputToken] = useState(existingConfig?.inputToken ?? TOKEN_OPTIONS[0]?.address ?? '')
  const [outputToken, setOutputToken] = useState(existingConfig?.outputToken ?? TOKEN_OPTIONS[1]?.address ?? '')
  const [slippageBps, setSlippageBps] = useState(existingConfig?.slippageBps ?? 50)
  const [isSuccess, setIsSuccess] = useState(false)

  const inputLabel = STABLECOIN_LABELS[inputToken.toLowerCase()] ?? inputToken.slice(0, 10)
  const outputLabel = STABLECOIN_LABELS[outputToken.toLowerCase()] ?? outputToken.slice(0, 10)

  const filteredOutputOptions = useMemo(
    () => TOKEN_OPTIONS.filter((t) => t.address.toLowerCase() !== inputToken.toLowerCase()),
    [inputToken],
  )

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setIsSuccess(false)
      resetUpdate()
    }
    onOpenChange(nextOpen)
  }

  const handleSave = async () => {
    try {
      await updateHook({
        cardID: card.cardID,
        hookType: 'swap',
        chainID: hyperEVM.id,
        config: { inputToken, outputToken, slippageBps },
      })
      setIsSuccess(true)
    } catch {
      // handled by mutation error state
    }
  }

  const handleDelete = async () => {
    try {
      await deleteHook(card.cardID)
      setIsSuccess(true)
    } catch {
      // handled by mutation error state
    }
  }

  const error = updateError || deleteError

  if (mode === 'remove') {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent overlayClassName="bg-black" className="max-w-md">
          <DialogHeader>
            <DialogTitle>{isSuccess ? 'Hook Removed' : 'Remove Hook'}</DialogTitle>
          </DialogHeader>

          {isSuccess ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20">
                <Check className="h-6 w-6 text-green-400" />
              </div>
              <Typography variant="p" className="text-center text-muted-foreground text-sm">
                Hook has been removed from this card.
              </Typography>
              <Button onClick={() => handleClose(false)}>Done</Button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">
                <div className="flex items-start gap-3">
                  <Trash2 className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
                  <div>
                    <Typography variant="p" className="font-medium text-red-300 text-sm">
                      Remove post-settlement hook?
                    </Typography>
                    {existingConfig && (
                      <Typography variant="p" className="mt-1 text-muted-foreground text-xs">
                        Swap {inputLabel} → {outputLabel}, {(slippageBps / 100).toFixed(1)}% slippage
                      </Typography>
                    )}
                    <Typography variant="p" className="mt-2 text-muted-foreground text-xs">
                      Future card swipes will no longer trigger automatic token swaps after settlement.
                    </Typography>
                  </div>
                </div>
              </div>

              {error && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
                    <Typography variant="p" className="text-red-300 text-sm">
                      {error.message}
                    </Typography>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" onClick={() => handleClose(false)}>
                  Cancel
                </Button>
                <Button onClick={handleDelete} disabled={isDeleting} className="bg-red-500 text-white hover:bg-red-600">
                  {isDeleting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Removing...
                    </>
                  ) : (
                    'Remove Hook'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent overlayClassName="bg-black" className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isSuccess ? 'Hook Saved' : mode === 'edit' ? 'Edit Hook' : 'Add Hook'}</DialogTitle>
        </DialogHeader>

        {isSuccess ? (
          <div className="flex flex-col items-center gap-4 py-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20">
              <Check className="h-6 w-6 text-green-400" />
            </div>
            <Typography variant="p" className="text-center text-muted-foreground text-sm">
              Swap {inputLabel} → {outputLabel}, {(slippageBps / 100).toFixed(1)}% slippage
            </Typography>
            <Button onClick={() => handleClose(false)}>Done</Button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2">
              <Zap className="h-4 w-4 text-primary" />
              <Typography variant="p" className="font-medium text-sm">
                Swap Hook
              </Typography>
              <Typography variant="p" className="text-muted-foreground text-xs">
                Auto-swap tokens after settlement
              </Typography>
            </div>

            <div>
              <Typography variant="p" className="mb-2 font-medium text-sm">
                Input Token (swap from)
              </Typography>
              <select
                value={inputToken}
                onChange={(e) => {
                  setInputToken(e.target.value)
                  if (e.target.value.toLowerCase() === outputToken.toLowerCase()) {
                    const alt = TOKEN_OPTIONS.find((t) => t.address.toLowerCase() !== e.target.value.toLowerCase())
                    if (alt) setOutputToken(alt.address)
                  }
                }}
                className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm focus:border-primary focus:outline-none"
              >
                {TOKEN_OPTIONS.map((t) => (
                  <option key={t.address} value={t.address}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Typography variant="p" className="mb-2 font-medium text-sm">
                Output Token (swap to)
              </Typography>
              <select
                value={outputToken}
                onChange={(e) => setOutputToken(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm focus:border-primary focus:outline-none"
              >
                {filteredOutputOptions.map((t) => (
                  <option key={t.address} value={t.address}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <Typography variant="p" className="font-medium text-sm">
                  Max Slippage
                </Typography>
                <Typography variant="p" className="text-muted-foreground text-sm">
                  {(slippageBps / 100).toFixed(1)}%
                </Typography>
              </div>
              <input
                type="range"
                min={10}
                max={200}
                step={5}
                value={slippageBps}
                onChange={(e) => setSlippageBps(Number(e.target.value))}
                className="w-full accent-primary"
              />
              <div className="mt-1 flex justify-between text-muted-foreground text-xs">
                <span>0.1%</span>
                <span>2.0%</span>
              </div>
            </div>

            <div className="rounded-lg border border-border p-3">
              <Typography variant="p" className="text-muted-foreground text-xs">
                After each card swipe settles, {inputLabel} will be swapped to {outputLabel} with a max slippage of{' '}
                {(slippageBps / 100).toFixed(1)}%.
              </Typography>
            </div>

            {error && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
                  <Typography variant="p" className="text-red-300 text-sm">
                    {error.message}
                  </Typography>
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isUpdating || inputToken === outputToken}>
                {isUpdating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : mode === 'edit' ? (
                  'Update Hook'
                ) : (
                  'Add Hook'
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
